import React, { useState } from 'react';
import { LogIn, ShieldCheck, Wallet, Clock, Info, ChevronDown, CheckCircle2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import { getBrandClasses } from '../../config/tenantConfig';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';

/**
 * Daily operating instructions shown to marketplace subscribers.
 *
 * Single source of truth for the instruction copy — rendered as a one-time
 * post-subscribe `modal` and as a persistent collapsible `banner` on the
 * My Subscriptions page. Copy intentionally omits the auto-login mechanism and
 * frames the daily action as a manual login by 9:10 AM (a buffer before the
 * 9:16 AM session gate enforced by the orchestration engine).
 */

const ACK_KEY = 'mp_daily_instructions_ack';

/** True unless the user previously ticked "Don't show again" on the modal. */
export const shouldShowDailyInstructions = (): boolean => {
  try {
    return localStorage.getItem(ACK_KEY) !== '1';
  } catch {
    return true;
  }
};

/** Persist the "Don't show again" choice (per-browser). */
export const ackDailyInstructions = (): void => {
  try {
    localStorage.setItem(ACK_KEY, '1');
  } catch {
    /* ignore storage failures — worst case the modal shows again */
  }
};

interface Step {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}

const STEPS: Step[] = [
  {
    icon: LogIn,
    title: 'Log in daily by 9:10 AM IST',
    body: 'Sign in to the platform every trading morning by 9:10 AM so your broker session is active before the market opens.',
  },
  {
    icon: ShieldCheck,
    title: 'Session must be valid by 9:16 AM',
    body: "If your broker session isn't valid by 9:16 AM, that account is skipped for the whole trading day and no orders are placed.",
  },
  {
    icon: Wallet,
    title: 'Keep sufficient capital',
    body: 'Maintain a linked broker account with enough funds for the subscribed strategy’s lot size.',
  },
  {
    icon: Clock,
    title: 'Strategies run 09:16–15:39 IST',
    body: 'Entries and exits fire automatically during market hours, Monday to Friday — no manual action needed during the day.',
  },
];

const StepList: React.FC = () => {
  const brand = getBrandClasses();
  return (
    <ul className="space-y-3">
      {STEPS.map(({ icon: Icon, title, body }) => (
        <li key={title} className="flex items-start gap-3">
          <span className={cn('mt-0.5 flex-shrink-0 rounded-md p-1.5', brand.iconBg)}>
            <Icon className={cn('h-4 w-4', brand.iconText)} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">{body}</p>
          </div>
        </li>
      ))}
    </ul>
  );
};

interface DailyInstructionsProps {
  /** `modal` = one-time post-subscribe dialog; `banner` = persistent collapsible card. */
  variant: 'modal' | 'banner';
  /** Modal only: controls visibility. */
  isOpen?: boolean;
  /** Modal only: invoked on dismiss. */
  onClose?: () => void;
}

const DailyInstructions: React.FC<DailyInstructionsProps> = ({ variant, isOpen, onClose }) => {
  const brand = getBrandClasses();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (variant === 'modal') {
    const handleClose = () => {
      if (dontShowAgain) ackDailyInstructions();
      onClose?.();
    };
    return (
      <Modal isOpen={!!isOpen} onClose={handleClose} maxWidth="lg" ariaLabel="Daily operating instructions">
        <div className="p-5 space-y-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CheckCircle2 className={cn('h-5 w-5', brand.iconText)} />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">You&rsquo;re subscribed</h2>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              A quick daily routine keeps this strategy running smoothly:
            </p>
          </div>

          <StepList />

          <div className="flex items-center justify-between gap-3 pt-2">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              />
              Don&rsquo;t show this again
            </label>
            <Button variant="primary" onClick={handleClose}>
              Got it
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // banner
  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 p-4 text-left"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-2 text-sm font-medium text-blue-800 dark:text-blue-200">
          <Info className="h-4 w-4 flex-shrink-0" />
          Daily routine to keep your subscriptions running
        </span>
        <ChevronDown
          className={cn('h-4 w-4 text-blue-600 dark:text-blue-300 transition-transform', expanded && 'rotate-180')}
        />
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          <StepList />
        </div>
      )}
    </div>
  );
};

export default DailyInstructions;
